import { AerFieldset } from "@aerstudios/aerated-aerfieldset";
import type {
	DefaultProps,
	HideableTextShape,
	LabelProps,
	Selectable,
} from "@aerstudios/aerated-types/types";
import {
	elementIsHideableTextShape,
	implementsTheSelectableInterface,
	isLabelProps,
} from "@aerstudios/aerated-utilities/dataStructures";
import { hyphenJoin } from "@aerstudios/aerated-utilities/stringUtils";
import { DotFilledIcon } from "@radix-ui/react-icons";
import cx from "clsx";
import React, {
	type ReactElement,
	type ReactNode,
	forwardRef,
	isValidElement,
	useCallback,
	useMemo,
	useState,
} from "react";
import * as styles from "./AerRadioGroup.module.scss";

const normalisedStyles = "default" in styles ? styles.default : styles;

export type AerRadioButtonProps = DefaultProps<"input"> &
	Selectable & {
		/** Required label for the radio. NOTE: you can hide the label using the `LabelProps` API, but must provide a label to make the field accessible. */
		label: string | ReactElement | LabelProps;
		/** The element to use as the radio background. NOTE: to hide the box altogether, please provide an empty element. */
		radioBackground?: ReactElement | null;
		/** The element to use for the checked state. */
		checkedIcon?: ReactElement | null;
		/** The AerRadioGroup.Item will not accept children. Please add all content in the `label` prop. */
		children?: never;
		/** NOTE: The onChange event is provided by the AerRadioGroup, anything that you provide will be called after the `onChange` handler passed to that. */
		onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	};
/**
 * AerRadioGroup.Item is a typical radio input.
 */
export const AerRadioButton = forwardRef(
	(
		{
			className,
			value,
			label,
			radioBackground,
			checked,
			checkedIcon = <DotFilledIcon className={normalisedStyles.checkedIcon} />,
			onChange,
			...rest
		}: AerRadioButtonProps,
		ref: React.ForwardedRef<HTMLInputElement>,
	) => {
		const dataDisabled: Record<string, true | never> = {};
		if ("disabled" in rest) {
			dataDisabled["data-disabled"] = true;
		}
		return (
			<label
				className={cx(normalisedStyles.radioInput, className)}
				{...dataDisabled}
			>
				<span className={cx(normalisedStyles.indicator)}>
					<input
						// biome-ignore lint/a11y/useAriaPropsForRole: aria-checked is added by the library
						// biome-ignore lint/a11y/noRedundantRoles: <explanation>
						role="radio"
						type="radio"
						{...rest}
						checked={checked}
						ref={ref}
						className={cx(normalisedStyles.input, className)}
						onChange={onChange}
					/>
					<span
						aria-hidden
						className={cx(normalisedStyles.imgWrapper, {
							[normalisedStyles.radioBackground]:
								!radioBackground && radioBackground !== null,
						})}
					>
						{radioBackground}
						{checked && checkedIcon}
					</span>
				</span>
				<span
					className={cx(normalisedStyles.label, {
						[normalisedStyles.visuallyHidden]:
							isLabelProps(label) && label.hidden,
					})}
				>
					{isLabelProps(label) ? label.name : label}
				</span>
			</label>
		);
	},
);

export type AerRadioGroupProps = DefaultProps<"fieldset"> & {
	value: string;
	groupLabel: string | HideableTextShape<string>;
	groupLabelClassName?: string;
	/** Ensure this is memoised */
	onChange?: (value: string) => void;
};

/**
 * AerRadioGroup allows automatic control of accessible radio buttons
 */
export const AerRadioGroup = forwardRef(
	(
		{
			className,
			groupLabelClassName,
			children,
			onChange,
			groupLabel,
			value,
			unselectable,
			...rest
		}: AerRadioGroupProps,
		ref: React.ForwardedRef<HTMLFieldSetElement>,
	) => {
		const [selected, setSelected] = useState<string>(value);
		const defaultName =
			rest.name ??
			hyphenJoin(
				elementIsHideableTextShape(groupLabel) ? groupLabel.text : groupLabel,
			);

		const getHandleChange = useCallback(
			(
				value: string,
				cb?: ((ev: React.ChangeEvent<HTMLInputElement>) => unknown) | null,
			) =>
				(ev: React.ChangeEvent<HTMLInputElement>) => {
					if (unselectable && selected === value) {
						setSelected("");
						onChange?.("");
					} else {
						setSelected(value);
						onChange?.(value);
					}
					cb?.(ev);
				},
			[onChange, unselectable, selected],
		);

		const trainedChildren = useMemo(() => {
			const findRadioButtonsAndApplyProps = (node: ReactNode): ReactNode => {
				return React.Children.map(node, (child): ReactNode => {
					if (!isValidElement(child) || typeof child.props !== "object") {
						return child;
					}

					if (implementsTheSelectableInterface(child.props)) {
						const typedChild: ReactElement<AerRadioButtonProps> =
							child as ReactElement<AerRadioButtonProps>;

						const onChange = getHandleChange(
							typedChild.props.value,
							typedChild.props.onChange,
						);

						const checked = selected === typedChild.props.value;

						const clone = React.cloneElement(typedChild, {
							...typedChild.props,
							onChange: onChange,
							checked: checked,
							name: defaultName,
							unselectable: unselectable,
						});

						return clone;
					}

					// check the children as well
					return React.cloneElement(child, {
						...child.props,
						children: findRadioButtonsAndApplyProps(child.props.children),
					});
				});
			};

			return findRadioButtonsAndApplyProps(children);
		}, [selected, children, defaultName, getHandleChange, unselectable]);

		return (
			<AerFieldset
				ref={ref}
				label={groupLabel}
				className={cx(normalisedStyles.component, className)}
				labelClassName={groupLabelClassName}
			>
				{trainedChildren}
			</AerFieldset>
		);
	},
);

// Add display names for better debugging
AerRadioButton.displayName = "AerRadioButton";
AerRadioGroup.displayName = "AerRadioGroup";
